import { useState } from "react";
import axios from "axios";
import { CheckCircle, ChevronRight, ChevronLeft, MessageCircle, AlertCircle, Plus, Trash2, Users, HeartPulse, Bike } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_TALLAS = ["XS", "S", "M", "L", "XL", "XXL"];

// ── Shared UI components ──────────────────────────────────────────────────────
function Input({ label, name, value, onChange, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="block font-['Montserrat'] font-semibold text-[#0F172A] text-sm mb-1.5">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input type={type} name={name} value={value} onChange={onChange} required={required}
        placeholder={placeholder} data-testid={`input-${name}`}
        className="w-full h-11 border-2 border-[#E2E8F0] rounded-xl px-4 text-[#0F172A] font-['Montserrat'] text-sm bg-slate-50 focus:outline-none focus:border-[#0072A4] focus:bg-white transition-colors" />
    </div>
  );
}

function Textarea({ label, name, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block font-['Montserrat'] font-semibold text-[#0F172A] text-sm mb-1">{label}</label>
      <textarea name={name} value={value} onChange={onChange} placeholder={placeholder} rows={2}
        data-testid={`textarea-${name}`}
        className="w-full border-2 border-[#E2E8F0] rounded-xl px-4 py-2.5 text-[#0F172A] font-['Montserrat'] text-sm focus:outline-none focus:border-[#0072A4] transition-colors resize-none" />
    </div>
  );
}

function Checkbox({ label, name, checked, onChange, highlight }) {
  return (
    <label className={`flex items-start gap-3 cursor-pointer group rounded-lg p-2 transition-colors ${highlight && !checked ? "bg-orange-50 border border-orange-200" : ""}`}>
      <input type="checkbox" name={name} checked={checked} onChange={onChange}
        data-testid={`checkbox-${name}`} className="mt-1 w-4 h-4 accent-[#0072A4] flex-shrink-0" />
      <span className="font-['Montserrat'] text-[#475569] text-sm group-hover:text-[#0F172A] transition-colors">{label}</span>
    </label>
  );
}

const STEPS = [
  { title: "Participantes", subtitle: "Datos de los niños/as" },
  { title: "Semanas y extras", subtitle: "Selecciona tu opción" },
  { title: "Autorización", subtitle: "Firma y confirmación" },
];

const INITIAL_NINO = {
  nombre_nino: "", fecha_nacimiento: "", edad: "", dni_nino: "", talla_camiseta: "M",
  alergias: "", intolerancias: "", enfermedades_medicacion: "", observaciones: "",
  btt_bicicleta: false, btt_camaras_gel: false, btt_camara_repuesto: false,
};

const INITIAL = {
  ninos: [{ ...INITIAL_NINO }],
  direccion: "", poblacion: "",
  nombre_tutor: "", telefono_contacto: "", email_contacto: "",
  semana_1: false, semana_2: false,
  madrugadores: false, recogida_ampliada: false,
  autoriza_participacion: false, autoriza_imagenes: true,
  nombre_firmante: "", fecha_firma: new Date().toISOString().split("T")[0],
};

export default function RegistrationForm({ pricing, general, form_config = {} }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [error, setError] = useState("");

  const moneda = pricing?.moneda || "€";
  const phone = (general?.whatsapp || "657871824").replace(/\D/g, "");
  const fc = form_config;
  const TALLAS = (fc?.tallas_camiseta?.length > 0) ? fc.tallas_camiseta : DEFAULT_TALLAS;

  // CMS labels with fallbacks
  const semana1Label = fc.semana1_label || "Semana 1: del 22 al 26 de Junio 2026";
  const semana2Label = fc.semana2_label || "Semana 2: del 29 de Junio al 3 de Julio 2026";
  const madrugadoresLabel = fc.madrugadores_label || "Servicio madrugadores (8:00h)";
  const recogidaLabel = fc.recogida_label || "Recogida ampliada (15:00h)";
  const bttLabel1 = fc.btt_label1 || "Traerá bicicleta en buen estado y con casco";
  const bttLabel2 = fc.btt_label2 || "Traerá cámaras con gel antipinchazos";
  const bttLabel3 = fc.btt_label3 || "Traerá una cámara de repuesto";
  const bttNote = fc.btt_note || "* Sin bicicleta en condiciones no se puede participar en BTT.";
  const authText = fc.auth_text || "Autorizo la participación del/la menor en todas las actividades del Campus Verano Activo La Estanca 2026 (kayak, paddle surf, orientación, BTT, aula de naturaleza, realidad aumentada), así como el uso de fotografías/vídeos para fines informativos o difusión en redes sociales.";
  const gdprText = fc.gdpr_text || "Los datos personales se utilizarán exclusivamente para la gestión del campus y no serán cedidos a terceros, conforme a la normativa vigente de protección de datos.";
  const btnNext = fc.btn_next || "Siguiente";
  const btnSubmit = fc.btn_submit || "Enviar inscripción";

  // ── Pricing calculation ───────────────────────────────────────────────────
  const numNinos = form.ninos.length;
  const pricePerNino = numNinos > 1
    ? (Number(pricing?.precio_hermanos) || 130)
    : (Number(pricing?.precio_semana) || 140);
  const numSemanas = (form.semana_1 ? 1 : 0) + (form.semana_2 ? 1 : 0);
  const extrasPorSemana =
    (form.madrugadores ? (Number(pricing?.precio_madrugadores) || 20) : 0) +
    (form.recogida_ampliada ? (Number(pricing?.precio_recogida) || 20) : 0);
  const precioIndividual = numSemanas * pricePerNino + numSemanas * extrasPorSemana;
  const calcTotal = () => precioIndividual * numNinos;

  // ── Child management ──────────────────────────────────────────────────────
  const addNino = () => setForm(prev => ({ ...prev, ninos: [...prev.ninos, { ...INITIAL_NINO }] }));
  const removeNino = (idx) => setForm(prev => ({ ...prev, ninos: prev.ninos.filter((_, i) => i !== idx) }));
  const onNinoChange = (idx, e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      ninos: prev.ninos.map((n, i) => i === idx ? { ...n, [name]: type === "checkbox" ? checked : value } : n)
    }));
  };

  // ── Shared field change ───────────────────────────────────────────────────
  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  // ── Step validation ───────────────────────────────────────────────────────
  const nextStep = () => {
    if (step === 0) {
      for (let i = 0; i < form.ninos.length; i++) {
        const n = form.ninos[i];
        if (!n.nombre_nino || !n.fecha_nacimiento || !n.edad) {
          setError(`Completa los datos obligatorios del niño/a ${i + 1}.`); return;
        }
        const anyBTT = n.btt_bicicleta || n.btt_camaras_gel || n.btt_camara_repuesto;
        const allBTT = n.btt_bicicleta && n.btt_camaras_gel && n.btt_camara_repuesto;
        if (anyBTT && !allBTT) {
          setError(`Para que ${n.nombre_nino || `el/la niño/a ${i + 1}`} participe en BTT debes confirmar las 3 condiciones de equipamiento.`); return;
        }
      }
      if (!form.nombre_tutor || !form.telefono_contacto || !form.email_contacto) {
        setError("Completa los datos del tutor/a."); return;
      }
    }
    if (step === 1 && !form.semana_1 && !form.semana_2) {
      setError("Selecciona al menos una semana."); return;
    }
    setError(""); setStep(s => s + 1);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.autoriza_participacion) { setError("Debes autorizar la participación."); return; }
    if (!form.nombre_firmante) { setError("Indica el nombre del firmante."); return; }
    setSubmitting(true); setError("");
    try {
      const payload = {
        ninos: form.ninos.map(n => ({
          ...n,
          edad: parseInt(n.edad) || 0,
        })),
        nombre_tutor: form.nombre_tutor,
        telefono_contacto: form.telefono_contacto,
        email_contacto: form.email_contacto,
        direccion: form.direccion,
        poblacion: form.poblacion,
        semana_1: form.semana_1,
        semana_2: form.semana_2,
        madrugadores: form.madrugadores,
        recogida_ampliada: form.recogida_ampliada,
        autoriza_participacion: form.autoriza_participacion,
        autoriza_imagenes: form.autoriza_imagenes,
        nombre_firmante: form.nombre_firmante,
        fecha_firma: form.fecha_firma,
      };
      const res = await axios.post(`${API}/registrations`, payload);
      setSuccessData(res.data);
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Error al enviar. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ────────────────────────────────────────────────────────
  if (success) {
    const nombresNinos = form.ninos.map(n => n.nombre_nino).filter(Boolean).join(", ");
    const totalFamilia = successData?.precio_total_familia || calcTotal();
    const msg = encodeURIComponent(`Hola, acabo de inscribir a ${nombresNinos} en el Campus Verano Activo 2026. ¿Podéis confirmar la disponibilidad de plaza?`);
    return (
      <section id="inscripcion" className="py-14 bg-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="bg-[#F0F8FF] rounded-3xl p-10 md:p-14 border border-[#3CB44B]/30">
            <CheckCircle size={72} color="#3CB44B" className="mx-auto mb-6" />
            <h2 className="font-['Montserrat'] font-bold text-xl md:text-2xl text-[#0F172A] mb-3" data-testid="registration-success-title">
              {numNinos > 1 ? `¡${numNinos} inscripciones recibidas!` : "¡Inscripción recibida!"}
            </h2>
            <p className="font-['Montserrat'] text-[#475569] mb-2">
              Hemos registrado la inscripción de <strong>{nombresNinos}</strong>.
            </p>
            <p className="font-['Montserrat'] text-[#475569] mb-6">
              Antes de realizar el pago, confirma disponibilidad de plaza por WhatsApp.
            </p>
            <div className="bg-[#F5C518]/10 border border-[#F5C518]/30 rounded-xl p-4 mb-8 text-sm text-[#475569] font-['Montserrat']">
              <strong className="text-[#0F172A]">Importe estimado:</strong> {totalFamilia}{moneda}
              {numNinos > 1 && <span className="text-[#3CB44B] ml-2">({precioIndividual}{moneda}/niño{numNinos > 1 ? ` × ${numNinos}` : ""})</span>}
              <br />Una vez confirmada la plaza, tienes <strong>24 horas</strong> para completar el pago.
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`https://wa.me/34${phone}?text=${msg}`} target="_blank" rel="noopener noreferrer"
                data-testid="success-whatsapp-btn"
                className="inline-flex items-center gap-2 bg-[#3CB44B] text-white font-['Montserrat'] font-bold px-8 py-3 rounded-full hover:bg-[#2E9B3A] transition-all">
                <MessageCircle size={18} /> Confirmar por WhatsApp
              </a>
              <button onClick={() => { setSuccess(false); setStep(0); setForm(INITIAL); setSuccessData(null); }}
                data-testid="new-registration-btn"
                className="font-['Montserrat'] font-semibold text-[#0072A4] border-2 border-[#0072A4] px-8 py-3 rounded-full hover:bg-[#0072A4] hover:text-white transition-all">
                Nueva inscripción
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="inscripcion" className="py-14 md:py-20 bg-[#F0F8FF]">
      <div className="max-w-2xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="text-center mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 bg-[#0072A4]/10 rounded-full px-5 py-2 mb-5">
            <CheckCircle size={16} color="#0072A4" />
            <span className="text-[#0072A4] font-['Montserrat'] font-bold text-[10px] uppercase tracking-[0.18em]">
              {fc.section_label || "Formulario de Inscripción"}
            </span>
          </div>
          <h2 className="font-['Montserrat'] font-extrabold text-[#0F172A] mb-5"
            style={{ fontSize: "clamp(1.25rem, 1.8vw, 1.625rem)", letterSpacing: "-0.01em", lineHeight: 1.2 }}
            data-testid="registration-title">
            {fc.title || "¡Reserva tu plaza!"}
          </h2>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((s, i) => (
            <div key={s.title} className="flex items-center flex-1">
              <div className={`flex flex-col items-center ${i < STEPS.length - 1 ? "flex-1" : ""}`}>
                <div data-testid={`step-indicator-${i}`}
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-['Montserrat'] font-bold text-sm transition-all ${i < step ? "bg-[#3CB44B] text-white" : i === step ? "bg-[#0072A4] text-white" : "bg-[#E2E8F0] text-[#94A3B8]"}`}>
                  {i < step ? <CheckCircle size={16} /> : i + 1}
                </div>
                <span className={`text-xs font-['Montserrat'] mt-1 hidden sm:block ${i === step ? "text-[#0072A4] font-semibold" : "text-[#94A3B8]"}`}>{s.title}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 mx-2 transition-all ${i < step ? "bg-[#3CB44B]" : "bg-[#E2E8F0]"}`} />}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-7 sm:p-10 shadow-xl border border-[#E2E8F0]" data-testid="registration-form">

          {/* ── Step 0: Participantes ──────────────────────────────────── */}
          {step === 0 && (
            <div className="space-y-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-['Montserrat'] font-bold text-base text-[#0072A4]">{STEPS[0].subtitle}</h3>
                <span className="text-xs font-['Montserrat'] text-[#94A3B8] bg-[#F0F8FF] px-2.5 py-1 rounded-full">
                  {numNinos} {numNinos === 1 ? "niño/a" : "niños/as"}
                </span>
              </div>

              {form.ninos.map((nino, idx) => {
                const anyBTT = nino.btt_bicicleta || nino.btt_camaras_gel || nino.btt_camara_repuesto;
                return (
                  <div key={idx} data-testid={`nino-card-${idx}`}
                    className="bg-[#F0F8FF] rounded-2xl border border-[#0072A4]/15 overflow-hidden">
                    {/* Card header */}
                    <div className="flex items-center justify-between px-5 py-3 bg-[#0072A4]/8 border-b border-[#0072A4]/10">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-[#0072A4] flex items-center justify-center">
                          <span className="text-white font-['Montserrat'] font-bold text-xs">{idx + 1}</span>
                        </div>
                        <span className="font-['Montserrat'] font-bold text-[#0072A4] text-sm">
                          {nino.nombre_nino ? nino.nombre_nino : `Niño/a ${idx + 1}`}
                        </span>
                      </div>
                      {form.ninos.length > 1 && (
                        <button type="button" onClick={() => removeNino(idx)}
                          data-testid={`remove-nino-${idx}`}
                          className="text-red-400 hover:text-red-600 transition-colors p-1">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>

                    <div className="p-5 space-y-4">
                      {/* Basic info */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                          <Input label="Nombre y apellidos" name="nombre_nino" value={nino.nombre_nino}
                            onChange={e => onNinoChange(idx, e)} required placeholder="Nombre completo" />
                        </div>
                        <Input label="Fecha de nacimiento" name="fecha_nacimiento" value={nino.fecha_nacimiento}
                          onChange={e => onNinoChange(idx, e)} required type="date" />
                        <Input label="Edad" name="edad" value={nino.edad}
                          onChange={e => onNinoChange(idx, e)} required type="number" placeholder="Años" />
                        <Input label="DNI (si tiene)" name="dni_nino" value={nino.dni_nino}
                          onChange={e => onNinoChange(idx, e)} placeholder="Opcional" />
                        <div>
                          <label className="block font-['Montserrat'] font-semibold text-[#0F172A] text-sm mb-1.5">
                            Talla camiseta <span className="text-red-500">*</span>
                          </label>
                          <select name="talla_camiseta" value={nino.talla_camiseta}
                            onChange={e => onNinoChange(idx, e)} data-testid={`select-talla-${idx}`}
                            className="w-full h-11 border-2 border-[#E2E8F0] rounded-xl px-4 text-[#0F172A] font-['Montserrat'] text-sm bg-white focus:outline-none focus:border-[#0072A4] transition-colors">
                            {TALLAS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                      </div>

                      {/* Health section */}
                      <div className="border-t border-[#0072A4]/10 pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <HeartPulse size={14} className="text-[#0072A4]" />
                          <p className="font-['Montserrat'] font-semibold text-xs text-[#475569] uppercase tracking-wider">
                            Información médica
                          </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Textarea label="Alergias" name="alergias" value={nino.alergias}
                            onChange={e => onNinoChange(idx, e)} placeholder="Alergias conocidas (si las hay)" />
                          <Textarea label="Intolerancias alimentarias" name="intolerancias" value={nino.intolerancias}
                            onChange={e => onNinoChange(idx, e)} placeholder="Intolerancias (si las hay)" />
                          <Textarea label="Enfermedades / Medicación" name="enfermedades_medicacion" value={nino.enfermedades_medicacion}
                            onChange={e => onNinoChange(idx, e)} placeholder="Medicación habitual o enfermedades" />
                          <Textarea label="Observaciones" name="observaciones" value={nino.observaciones}
                            onChange={e => onNinoChange(idx, e)} placeholder="Cualquier información relevante" />
                        </div>
                      </div>

                      {/* BTT section */}
                      <div className="border-t border-[#0072A4]/10 pt-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Bike size={14} className="text-[#F58220]" />
                            <p className="font-['Montserrat'] font-semibold text-xs text-[#475569] uppercase tracking-wider">
                              Actividad BTT (si participa)
                            </p>
                          </div>
                          {anyBTT && (!nino.btt_bicicleta || !nino.btt_camaras_gel || !nino.btt_camara_repuesto) && (
                            <span className="text-xs font-['Montserrat'] text-orange-600 font-semibold bg-orange-50 px-2 py-0.5 rounded-full">
                              Confirma los 3
                            </span>
                          )}
                        </div>
                        <div className="space-y-1 bg-[#F58220]/5 border border-[#F58220]/20 rounded-xl p-3">
                          <Checkbox label={bttLabel1} name="btt_bicicleta" checked={nino.btt_bicicleta}
                            onChange={e => onNinoChange(idx, e)} highlight={anyBTT} />
                          <Checkbox label={bttLabel2} name="btt_camaras_gel" checked={nino.btt_camaras_gel}
                            onChange={e => onNinoChange(idx, e)} highlight={anyBTT} />
                          <Checkbox label={bttLabel3} name="btt_camara_repuesto" checked={nino.btt_camara_repuesto}
                            onChange={e => onNinoChange(idx, e)} highlight={anyBTT} />
                          <p className="text-xs text-[#475569] font-['Montserrat'] mt-1 pl-2">{bttNote}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Add child button */}
              <button type="button" onClick={addNino} data-testid="add-nino-btn"
                className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#0072A4]/30 text-[#0072A4] font-['Montserrat'] font-semibold text-sm py-3 rounded-2xl hover:border-[#0072A4] hover:bg-[#F0F8FF] transition-all">
                <Plus size={16} /> Añadir otro niño/a
              </button>

              {/* Sibling discount notice */}
              {numNinos > 1 && (
                <div className="flex items-start gap-3 bg-[#3CB44B]/8 border border-[#3CB44B]/25 rounded-xl p-4">
                  <Users size={16} className="text-[#3CB44B] flex-shrink-0 mt-0.5" />
                  <p className="font-['Montserrat'] text-sm text-[#2E9B3A] font-semibold">
                    Descuento hermanos/as activo — {pricing?.precio_hermanos || 130}{moneda}/semana por cada uno de los {numNinos} inscritos.
                  </p>
                </div>
              )}

              {/* Tutor data */}
              <div className="border-t border-[#E2E8F0] pt-5 mt-2">
                <h3 className="font-['Montserrat'] font-semibold text-sm text-[#0F172A] mb-4">Datos del padre/madre/tutor</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <Input label="Nombre completo" name="nombre_tutor" value={form.nombre_tutor} onChange={onChange} required placeholder="Nombre y apellidos" />
                  </div>
                  <Input label="Teléfono de contacto" name="telefono_contacto" value={form.telefono_contacto} onChange={onChange} required type="tel" placeholder="600 000 000" />
                  <Input label="Correo electrónico" name="email_contacto" value={form.email_contacto} onChange={onChange} required type="email" placeholder="email@ejemplo.com" />
                  <Input label="Dirección" name="direccion" value={form.direccion} onChange={onChange} placeholder="Calle y número" />
                  <Input label="Población" name="poblacion" value={form.poblacion} onChange={onChange} placeholder="Localidad" />
                </div>
              </div>
            </div>
          )}

          {/* ── Step 1: Semanas y extras ───────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-6">
              <h3 className="font-['Montserrat'] font-bold text-base text-[#0072A4] mb-4">{STEPS[1].subtitle}</h3>
              <div>
                <p className="font-['Montserrat'] font-semibold text-[#0F172A] text-sm mb-3">Semana(s) de inscripción *</p>
                <div className="space-y-3">
                  <Checkbox label={semana1Label} name="semana_1" checked={form.semana_1} onChange={onChange} />
                  <Checkbox label={semana2Label} name="semana_2" checked={form.semana_2} onChange={onChange} />
                </div>
              </div>
              <div className="border-t border-[#E2E8F0] pt-4">
                <p className="font-['Montserrat'] font-semibold text-[#0F172A] text-sm mb-3">Servicios opcionales</p>
                <div className="space-y-3">
                  <Checkbox
                    label={`${madrugadoresLabel} — +${pricing?.precio_madrugadores || 20}${moneda}/semana/niño`}
                    name="madrugadores" checked={form.madrugadores} onChange={onChange} />
                  <Checkbox
                    label={`${recogidaLabel} — +${pricing?.precio_recogida || 20}${moneda}/semana/niño`}
                    name="recogida_ampliada" checked={form.recogida_ampliada} onChange={onChange} />
                </div>
              </div>
              {(form.semana_1 || form.semana_2) && (
                <div className="bg-[#F5C518]/10 border border-[#F5C518]/30 rounded-xl p-5 space-y-2" data-testid="price-breakdown">
                  <p className="font-['Montserrat'] font-bold text-[#0F172A] text-sm mb-3">Resumen de precio estimado</p>
                  <div className="text-xs font-['Montserrat'] text-[#475569] space-y-1.5">
                    {form.ninos.map((n, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{n.nombre_nino || `Niño/a ${i+1}`} — {numSemanas} sem × {pricePerNino}{moneda}</span>
                        <span className="font-semibold text-[#0F172A]">{numSemanas * pricePerNino}{moneda}</span>
                      </div>
                    ))}
                    {form.madrugadores && (
                      <div className="flex justify-between">
                        <span>Madrugadores: {numNinos} × {numSemanas} × {pricing?.precio_madrugadores || 20}{moneda}</span>
                        <span className="font-semibold text-[#0F172A]">{numNinos * numSemanas * (Number(pricing?.precio_madrugadores) || 20)}{moneda}</span>
                      </div>
                    )}
                    {form.recogida_ampliada && (
                      <div className="flex justify-between">
                        <span>Recogida ampliada: {numNinos} × {numSemanas} × {pricing?.precio_recogida || 20}{moneda}</span>
                        <span className="font-semibold text-[#0F172A]">{numNinos * numSemanas * (Number(pricing?.precio_recogida) || 20)}{moneda}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-[#F5C518]/40 font-bold text-base text-[#F58220]">
                      <span>Total estimado ({numNinos} {numNinos > 1 ? "niños/as" : "niño/a"})</span>
                      <span data-testid="price-total">{calcTotal()}{moneda}</span>
                    </div>
                  </div>
                  {numNinos > 1 && (
                    <p className="text-xs text-[#3CB44B] font-['Montserrat'] font-semibold mt-1">
                      Descuento hermanos/as aplicado: {pricePerNino}{moneda}/semana/niño
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Step 2: Autorización ───────────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-5">
              <h3 className="font-['Montserrat'] font-bold text-base text-[#0072A4] mb-4">{STEPS[2].subtitle}</h3>
              <div className="bg-[#F0F8FF] rounded-xl p-4 text-sm text-[#475569] font-['Montserrat'] leading-relaxed">
                <strong className="text-[#0F172A]">Autorización de participación:</strong> {authText}
              </div>
              <div className="bg-[#F0F8FF] rounded-xl p-4 text-sm text-[#475569] font-['Montserrat'] leading-relaxed">
                <strong className="text-[#0F172A]">Protección de datos:</strong> {gdprText}
              </div>
              {/* Summary of who is being authorized */}
              {form.ninos.length > 0 && (
                <div className="bg-white border border-[#0072A4]/20 rounded-xl p-4">
                  <p className="font-['Montserrat'] font-semibold text-xs text-[#0072A4] uppercase tracking-wider mb-2">Esta autorización aplica a:</p>
                  <div className="space-y-1">
                    {form.ninos.map((n, i) => (
                      <p key={i} className="font-['Montserrat'] text-sm text-[#0F172A]">
                        {i + 1}. <strong>{n.nombre_nino || `Niño/a ${i + 1}`}</strong>
                      </p>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-3">
                <Checkbox label="Autorizo la participación en todas las actividades del campus *" name="autoriza_participacion" checked={form.autoriza_participacion} onChange={onChange} />
                <div>
                  <p className="font-['Montserrat'] font-semibold text-sm text-[#0F172A] mb-2">Uso de imágenes y vídeos:</p>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="autoriza_imagenes" checked={form.autoriza_imagenes === true} onChange={() => setForm(p => ({ ...p, autoriza_imagenes: true }))} data-testid="radio-imagenes-si" className="accent-[#0072A4]" />
                      <span className="font-['Montserrat'] text-sm text-[#475569]">Autorizo</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="autoriza_imagenes" checked={form.autoriza_imagenes === false} onChange={() => setForm(p => ({ ...p, autoriza_imagenes: false }))} data-testid="radio-imagenes-no" className="accent-[#0072A4]" />
                      <span className="font-['Montserrat'] text-sm text-[#475569]">No autorizo</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-[#E2E8F0]">
                <Input label="Nombre del firmante *" name="nombre_firmante" value={form.nombre_firmante} onChange={onChange} required placeholder="Nombre completo" />
                <Input label="Fecha de la firma *" name="fecha_firma" value={form.fecha_firma} onChange={onChange} required type="date" />
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 mt-4" data-testid="form-error">
              <AlertCircle size={16} color="#EF4444" />
              <p className="font-['Montserrat'] text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-[#E2E8F0]">
            <button type="button" onClick={() => { setStep(s => s - 1); setError(""); }}
              className={`flex items-center gap-2 font-['Montserrat'] font-semibold text-[#475569] hover:text-[#0072A4] transition-colors px-4 py-2 ${step === 0 ? "invisible" : ""}`}
              data-testid="prev-step-btn">
              <ChevronLeft size={18} /> Anterior
            </button>
            {step < STEPS.length - 1 ? (
              <button type="button" onClick={nextStep} data-testid="next-step-btn"
                className="flex items-center gap-2 bg-[#0072A4] text-white font-['Montserrat'] font-bold px-8 py-3 rounded-full hover:bg-[#005A80] active:scale-95 transition-all">
                {btnNext} <ChevronRight size={18} />
              </button>
            ) : (
              <button type="submit" disabled={submitting} data-testid="submit-registration-btn"
                className="flex items-center gap-2 bg-[#3CB44B] text-white font-['Montserrat'] font-bold px-8 py-3 rounded-full hover:bg-[#2E9B3A] active:scale-95 transition-all disabled:opacity-60">
                {submitting ? "Enviando..." : btnSubmit}
                {!submitting && <CheckCircle size={18} />}
              </button>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
